import styled from 'styled-components';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../media/Dice_logo_finished-1.png';
import Cover from '../media/TripleFreezerCover2.jpg';
import { FaDice, FaCoins, FaMapMarkerAlt, FaTrophy } from 'react-icons/fa';
import { AuthContext } from '../AuthContext';
import { subscribeAllTournaments, getPlayerActiveMatch } from '../services/tournamentService';
import { Tournament, TournamentMatch } from '../types';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;  /* Ensure background covers the full width */
  background: url(${Cover}) no-repeat center center;
  background-size: cover;
  position: relative;
`;

// Header bar with correct color
const Header = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 40px;
  background: rgb(0, 17, 26);
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 0 20px;
  z-index: 10;
`;

const HeaderTitle = styled.h1`
  font-family: 'Fjalla One', sans-serif;
  font-size: 16px;
  color: rgb(255, 255, 255);
  margin: 0;
  text-transform: uppercase;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 60px; /* Space for header */
  padding: ${({ theme }) => theme.spacing(2)};
  width: 90%;
  max-width: 400px;
  text-align: center;
`;

const LogoImage = styled.img`
  width: 120px;
  height: auto;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

// Welcome section with semi-transparent background
const WelcomeSection = styled.div`
  /* Figma specs for Info section */
  width: 320px;
  height: 180px;
  border-radius: 10px;
  background: rgba(0, 43, 64, 0.5);
  padding: 20px;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h2`
  /* Figma specs for Welcome title */
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 23px;
  color: rgb(255, 255, 255);
  margin: 0 0 15px 0;
  text-transform: uppercase;
  text-align: center;
`;

const Description = styled.p`
  /* Figma specs for description text */
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  color: rgb(255, 255, 255);
  margin: 0;
  text-align: center;
  width: 300px;
`;


const Button = styled.button`
  /* Figma button specs */
  width: 200px;
  height: 40px;
  border-radius: 5px;
  background: linear-gradient(180deg, rgb(0, 213, 255), rgb(0, 43, 64) 100%);
  color: rgb(255, 255, 255);
  border: none;
  cursor: pointer;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  font-family: 'Fjalla One', sans-serif;
  font-size: 14px;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 213, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  svg {
    font-size: 18px;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 213, 255, 0.4);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 10px rgba(0, 213, 255, 0.3);
  }

  /* Responsive adjustments */
  @media (max-width: 480px) {
    width: 100%;
    max-width: 200px;
  }
`;

const ActiveMatchBanner = styled.div`
  width: 320px;
  border-radius: 8px;
  background: rgba(0, 200, 100, 0.15);
  border: 1px solid rgba(0, 200, 100, 0.5);
  padding: 12px 16px;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(0, 200, 100, 0.25);
  }
`;

const BannerText = styled.div`
  flex: 1;
`;

const BannerTitle = styled.div`
  font-family: 'Fjalla One', sans-serif;
  font-size: 14px;
  color: #00C864;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const BannerSub = styled.div`
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 2px;
`;

const Home: React.FC = () => {
  const navigate = useNavigate();
  const contentWrapperRef = React.useRef<HTMLDivElement>(null);
  const { isAuthenticated, user } = useContext(AuthContext);
  const [activeMatch, setActiveMatch] = useState<{ match: TournamentMatch; tournament: Tournament } | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const unsub = subscribeAllTournaments((tournaments) => {
      // Find the first active/pending match across all ongoing tournaments
      for (const t of tournaments) {
        if (t.status === 'completed' || t.status === 'draft') continue;
        const match = getPlayerActiveMatch(user.uid, t);
        if (match && match.player2) {
          setActiveMatch({ match, tournament: t });
          return;
        }
      }
      setActiveMatch(null);
    });

    return () => unsub();
  }, [isAuthenticated, user]);

  return (
    <Container>
      {/* Header */}
      <Header>
        <HeaderTitle>TRIPLE FREEZER</HeaderTitle>
      </Header>

      <ContentWrapper ref={contentWrapperRef}>
        <LogoImage src={Logo} alt="Triple Freezer dice game logo" width={120} height={120} />
        
        {/* Welcome section with semi-transparent background */}
        <WelcomeSection>
          <Title>WELCOME TO TRIPLE FREEZER</Title>
          <Description>
            Triple Freezer is a thrilling dice game where strategy meets chance! Use our online tools to calculate your scores, keep track of your game statistics, find out about upcoming events, and learn the official rules. Join the community and elevate your Triple Freezer experience!{' '}
            <a
              href="/about"
              aria-label="Read more about Triple Freezer"
              style={{ color: 'rgb(0, 127, 255)', textDecoration: 'underline' }}
            >read more</a>
          </Description>
        </WelcomeSection>

        {/* AdSense banner removed: the home screen is a thin landing/navigation
            page (logo + blurb + buttons), which AdSense flags as "ads without
            publisher-content / low value content". Re-add ads only on
            content-rich pages once the site passes review. */}
        {activeMatch && (
          <ActiveMatchBanner onClick={() => navigate(`/tournaments/${activeMatch.tournament.id}`)}>
            <FaTrophy style={{ color: '#00C864', fontSize: 20, flexShrink: 0 }} />
            <BannerText>
              <BannerTitle>Tournament match waiting</BannerTitle>
              <BannerSub>
                {activeMatch.match.player1.name} vs {activeMatch.match.player2?.name} — {activeMatch.tournament.name}
              </BannerSub>
            </BannerText>
          </ActiveMatchBanner>
        )}

        <Button onClick={() => navigate('/lobby')}>
          <FaDice />
          PLAY THE GAME
        </Button>
        <Button onClick={() => navigate('/Buy')}>
          <FaCoins />
          BUY THE GAME
        </Button>
        <Button onClick={() => navigate('/where-to-play')}>
          <FaMapMarkerAlt />
          WHERE TO PLAY
        </Button>
        
      </ContentWrapper>
    </Container>
  );
};

export default Home;

